import { useRef, useState } from 'react'
import { LuCheck, LuCircleUser, LuInfo, LuListFilter, LuX } from 'react-icons/lu'

import { useClickOutside } from '@/lib/core/hooks/useClickOutside'
import { Tooltip, TooltipContainerProvider } from '@/lib/core/components/Tooltip'

import { ALERT_FILTER_TOOLTIPS } from '../CoreAlertsModal/coreAlertUtils'
import {
  type AlertFilters,
  countActiveFilters,
  DEFAULT_ALERT_FILTERS,
} from '../../types/alertFilters'
import styles from './AlertFilterPanel.module.css'

interface AlertFilterPanelProps {
  filters: AlertFilters
  onChange: (filters: AlertFilters) => void
}

const TagInput = ({
  tags,
  onAdd,
  onRemove,
  placeholder,
  icon,
}: {
  tags: string[]
  onAdd: (tag: string) => void
  onRemove: (index: number) => void
  placeholder?: string
  icon?: React.ReactNode
}) => {
  const [input, setInput] = useState('')

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      onAdd(input.trim())
      setInput('')
    }
    if (e.key === 'Backspace' && !input && tags.length > 0) {
      onRemove(tags.length - 1)
    }
  }

  return (
    <div className={styles.tagInput}>
      {tags.map((tag, i) => (
        <span key={`${tag}-${i}`} className={styles.tag}>
          {icon}
          {tag}
          <button type="button" className={styles.tagRemove} onClick={() => onRemove(i)}>
            <LuX size={16} />
          </button>
        </span>
      ))}
      <input
        className={styles.tagTextInput}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? placeholder : ''}
      />
    </div>
  )
}

const NumericTagInput = ({
  value,
  onChange,
  unit,
}: {
  value: number | null
  onChange: (value: number | null) => void
  unit: string
}) => {
  const [input, setInput] = useState('')

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault()
      const num = parseInt(input.trim(), 10)
      if (!isNaN(num) && num > 0) {
        onChange(num)
        setInput('')
      }
    }
  }

  return (
    <div className={styles.numericInputWrapper}>
      <div className={styles.numericInputSection}>
        {value !== null ? (
          <span className={styles.tag}>
            {value}
            <button type="button" className={styles.tagRemove} onClick={() => onChange(null)}>
              <LuX size={16} />
            </button>
          </span>
        ) : (
          <input
            className={styles.numericInput}
            type="number"
            min={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        )}
      </div>
      <div className={styles.numericUnit}>{unit}</div>
    </div>
  )
}

const FilterCheckbox = ({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
}) => {
  const id = `filter-checkbox-${label.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <label htmlFor={id} className={styles.checkboxRow}>
      <input
        id={id}
        type="checkbox"
        className={styles.checkboxHidden}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={[styles.checkboxBox, checked ? styles.checkboxBoxChecked : ''].join(' ')}>
        {checked && <LuCheck size={14} />}
      </span>
      <span className={styles.checkboxLabel}>{label}</span>
    </label>
  )
}

export const AlertFilterPanel = ({ filters, onChange }: AlertFilterPanelProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useClickOutside([panelRef], () => setIsOpen(false), isOpen)

  const activeCount = countActiveFilters(filters)

  const handleClearAll = () => {
    onChange(DEFAULT_ALERT_FILTERS)
  }

  return (
    <div className={styles.filterWrapper} ref={panelRef}>
      <button
        type="button"
        className={[styles.filterTab, isOpen ? styles.active : ''].join(' ')}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <LuListFilter size={16} />
        Filter
        {activeCount > 0 && <span className={styles.filterBadge}>{activeCount}</span>}
        {activeCount > 0 && (
          <span
            className={styles.filterTabClose}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation()
              handleClearAll()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation()
                handleClearAll()
              }
            }}
          >
            <LuX size={16} />
          </span>
        )}
      </button>

      {isOpen && (
        <div className={styles.panel} ref={setPanelElement}>
          <TooltipContainerProvider container={panelElement}>
            <div className={styles.panelHeader}>
              <button type="button" className={styles.clearAll} onClick={handleClearAll}>
                Clear all
              </button>
            </div>

            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                <span className={styles.filterLabelText}>Alert Title</span>
                <Tooltip
                  tooltipContent={ALERT_FILTER_TOOLTIPS.alertTitle}
                  placement="top"
                  maxWidth={300}
                >
                  <span>
                    <LuInfo size={16} className={styles.filterLabelInfo} />
                  </span>
                </Tooltip>
              </div>
              <TagInput
                tags={filters.titleTags}
                onAdd={(tag) => onChange({ ...filters, titleTags: [...filters.titleTags, tag] })}
                onRemove={(i) =>
                  onChange({
                    ...filters,
                    titleTags: filters.titleTags.filter((_, idx) => idx !== i),
                  })
                }
                placeholder="Type and press Enter..."
              />
            </div>

            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                <span className={styles.filterLabelText}>Alert Contacts</span>
                <Tooltip
                  tooltipContent={ALERT_FILTER_TOOLTIPS.alertContacts}
                  placement="top"
                  maxWidth={300}
                >
                  <span>
                    <LuInfo size={16} className={styles.filterLabelInfo} />
                  </span>
                </Tooltip>
              </div>
              <TagInput
                tags={filters.contactTags}
                onAdd={(tag) =>
                  onChange({ ...filters, contactTags: [...filters.contactTags, tag] })
                }
                onRemove={(i) =>
                  onChange({
                    ...filters,
                    contactTags: filters.contactTags.filter((_, idx) => idx !== i),
                  })
                }
                placeholder="Type and press Enter..."
                icon={<LuCircleUser size={16} />}
              />
            </div>

            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                <span className={styles.filterLabelText}>Duration</span>
                <Tooltip
                  tooltipContent={ALERT_FILTER_TOOLTIPS.duration}
                  placement="top"
                  maxWidth={300}
                >
                  <span>
                    <LuInfo size={16} className={styles.filterLabelInfo} />
                  </span>
                </Tooltip>
              </div>
              <NumericTagInput
                value={filters.duration}
                onChange={(duration) => onChange({ ...filters, duration })}
                unit="mins"
              />
            </div>

            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                <span className={styles.filterLabelText}>Distance</span>
                <Tooltip
                  tooltipContent={ALERT_FILTER_TOOLTIPS.distance}
                  placement="top"
                  maxWidth={300}
                >
                  <span>
                    <LuInfo size={16} className={styles.filterLabelInfo} />
                  </span>
                </Tooltip>
              </div>
              <NumericTagInput
                value={filters.distance}
                onChange={(distance) => onChange({ ...filters, distance })}
                unit="km"
              />
            </div>

            <div className={styles.filterSection}>
              <div className={styles.filterLabel}>
                <span className={styles.filterLabelText}>Alert Enabled</span>
                <Tooltip
                  tooltipContent={ALERT_FILTER_TOOLTIPS.alertEnabled}
                  placement="top"
                  maxWidth={300}
                >
                  <span>
                    <LuInfo size={16} className={styles.filterLabelInfo} />
                  </span>
                </Tooltip>
              </div>
              <div className={styles.checkboxSection}>
                <FilterCheckbox
                  checked={filters.showDisabled}
                  onChange={(showDisabled) => onChange({ ...filters, showDisabled })}
                  label="Show Disabled alerts"
                />
                <FilterCheckbox
                  checked={filters.showEnabled}
                  onChange={(showEnabled) => onChange({ ...filters, showEnabled })}
                  label="Show Enabled alerts"
                />
                <FilterCheckbox
                  checked={filters.showNoContacts}
                  onChange={(showNoContacts) => onChange({ ...filters, showNoContacts })}
                  label="Show alerts with no contacts"
                />
              </div>
            </div>
          </TooltipContainerProvider>
        </div>
      )}
    </div>
  )
}
